import { ChannelRedirect } from '../components/admin/settings/channel-redirects';
import { ChannelPermission, Channel, Profile } from '../types/working-model';
import { Get, Put, Post, Delete } from './helpers';

export const authLogin = async (email: string, password: string, two_factor_stored: string | null): Promise<any> => {
	return Post('/v3/admin/auth/sign-in', '', { email, password, two_factor_stored: (two_factor_stored ? two_factor_stored : "") }, false, {}, true)
		.then(async res => {
			let response;

			try {
				response = await res.json();
			} catch {
				response = res.statusText;
			}

			if (response.needs_verification) {
				throw new Error('no password');
			}

			if (response.reset_required) {
				throw new Error('a password that is old');
			}

			if (response.no_channels) {
				throw new Error('no channels');
			}

			if (response.email_temporarily_locked) {
				throw new Error('email_temporarily_locked');
			}

			if (res.status === 403) {
				throw new Error('incorrect email/password');
			}

			return response;
		});
};

export const tempPhone = async (phone: string, temp_profile: string): Promise<any> => {
	return Post('/v3/admin/auth/set-temp-phone', '', { temp_profile, phone });
};

export const authCode = async (temp_profile: string, code: string, should_confirm: boolean, request_storage: boolean): Promise<any> => {
	return Post('/v3/admin/auth/confirm-code', '', { temp_profile, code, should_confirm, request_storage }, false, {}, true)
		.then(res => {
			if (res.status === 403) {
				throw new Error('incorrect confirmation code given');
			}

			const response = res.json();

			return response;
		});
};

export const ForgotPassword = async (email: string, hostname: string): Promise<any> => {
	return Post('/v3/admin/auth/forgot-password', '', { email, hostname }, false, {}, true)
		.then(res => {
			if (res.ok) {
				return res.ok;
			}

			throw new Error('Forgot Password Failure');
		});
};

export const UpdatePassword = async (password: string, code: string, cb: () => void): Promise<any> => {
	return Put('/v3/admin/auth/forgot-password', '', { password, code }, false, true)
		.then(res => {
			if (res.ok) {
				cb();

				return res.ok;
			}

			throw new Error('Password Reset Failure');
		});
};

export const RefreshToken = async (token: string): Promise<any> => {
	return Get('/v3/admin/auth/refresh-token', token, {}, true)
		.then(res => {
			if (res.ok) {
				return res.json();
			}

			throw new Error('Unable To Refresh Token');
		});
};

export function GetChannels(token: string): Promise<any> {
	return Get('/v3/admin/auth/channels', token);
}

export function SelectChannel(token: string, channel: number): Promise<any> {
	return Post('/v3/admin/auth/channels', token, { channel });
}

export function GetAdmins(token: string, includeLockedEmails = false): Promise<any[]> {
	return Get(`/v3/admin/auth/admins${includeLockedEmails ? '?option=includeLockedEmails' : ''}`, token);
}

export function UnlockEmail(token: string, email_to_unlock: string) {
	return Put('/v3/admin/auth/unlock-profile', token, { email_to_unlock }, true);
}

export function UpdateChannels(token: string, id: number, channels: ChannelPermission): Promise<any> {
	return Put(`/v3/admin/auth/update-channels`, token, { id, channels }, true);
}

export function UpdateChannel(token: string, channel: Channel): Promise<Channel> {
	return Put(`/v3/admin/auth/update-channel`, token, { channel }, true);
}

export const UpdateOnboarding = async (token: string, id: number, onboarding: string[], newItem: string): Promise<any> => {
	onboarding.push(newItem);

	return Put('/v3/admin/auth/update-onboarding', token, { id, onboarding }, false, true)
		.then(res => {
			if (res.ok) {
				return res.json();
			}

			throw new Error('Unable To Get Response');
		});
};

export function ResetOnboarding(token: string, id: number): Promise<any> {
	return Put(`/v3/admin/auth/clear-onboarding`, token, { id });
}

export function AddNewAdmin(token: string, email: string, permissions: string[]): Promise<any> {
	return Post(`/v3/admin/auth/add-admin`, token, { email, permissions });
}

export const SetAdminPassword = async (password: string, hashCode: string, cb: () => void): Promise<any> => {
	return Post('/v3/admin/auth/set-password', '', { hashCode, password }, false, {}, true)
		.then(res => {
			if (res.ok) {
				cb();

				return res.ok;
			}

			throw new Error('Password Set Failure');
		});
};

export const UpdateAdminProfile = async (token: string, profile: Profile): Promise<any> => {
	return Put('/v3/admin/auth/update-profile', token, { profile });
};

export function GetChannelRedirects(token: string, channel: number): Promise<any> {
	return Get(`/v3/admin/channel/redirects/${channel}`, token);
}

export function CreateChannelRedirect(token: string, redirect: Omit<ChannelRedirect, 'id'>): Promise<any> {
	return Post(`/v3/admin/channel/redirect`, token, redirect);
}

export function UpdateChannelRedirect(token: string, redirect: ChannelRedirect): Promise<any> {
	return Put(`/v3/admin/channel/redirect`, token, redirect, true);
}

export function DeleteChannelRedirect(token: string, redirect: ChannelRedirect): Promise<any> {
	return Delete(`/v3/admin/channel/redirect`, token, redirect);
}

export function CheckSignatureStatus(token: string, signature: number): Promise<boolean> {
	return Get(`/v3/admin/auth/check-signature-status/${signature}`, token);
}

export function ResendSignatureConfirmation(token: string, id: number): Promise<{ ErrorCode: number, Message: string; }> {
	return Post('/v3/admin/auth/resend-confirm-sender-signature', token, { id });
}

export function AddAdminToGroup(token: string, admin_id: number, group_uuid: string, channel: number): Promise<{ admin_id: number, group_uuid: string }> {
	return Put(`/v3/admin/auth/event-groups`, token, { admin_id, group_uuid, channel }, true);
}

export function RemoveAdminFromGroup(token: string, admin_id: number, group_uuid: string, channel: number): Promise<{ admin_id: number, group_uuid: string }> {
	return Delete(`/v3/admin/auth/event-groups`, token, { admin_id, group_uuid, channel }, true);
}

export function RemoveAdminFromAllGroups(token: string, admin_id: number, channel: number): Promise<{ admin_id: number, group_uuid: string }> {
	return Delete(`/v3/admin/auth/event-groups/all`, token, { admin_id, channel }, true);
}

export function PreviewEventSignIn(eventPreviewUuid: string, previewPasscode: string): Promise<{ error?: string, authenticated?: string }> {
	return Post(`/v3/admin/events/${eventPreviewUuid}/preview/sign-in`, "", { passcode: previewPasscode });
}